import i18n from '@/plugins/i18n'
import { useRegistryStore } from '@/stores/registry.store'
import { usePortalStore } from '@/stores/portal.store'

export const isNotEmpty = (value) => value !== undefined && value !== null && value !== ''

export const isObject = (item) => {
  return (item && typeof item === 'object' && !Array.isArray(item))
}

export const mergeDeep = (target, source) => {
  const output = Object.assign({}, target)
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target)) { Object.assign(output, { [key]: source[key] }) } else { output[key] = mergeDeep(target[key], source[key]) }
      } else {
        Object.assign(output, { [key]: source[key] })
      }
    })
  }
  return output
}

export const formatDate = (date, locale, showYear = true) => {
  const options = {
    month: '2-digit',
    day: '2-digit',
  }

  if (showYear) {
    options.year = 'numeric'
  }

  return new Intl.DateTimeFormat(locale, options).format(new Date(date))
}

export const formatTime = (time, locale, fromDateTime = true) => {
  let hours, minutes

  if (fromDateTime) {
    hours = time.getHours()
    minutes = time.getMinutes()
  } else {
    [hours, minutes] = time.includes('.') ? time.split('.') : time.split(':')
  }

  const date = new Date()
  date.setHours(hours, minutes || 0)

  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(date)
}

export const formatDateTime = (dateTime, locale, showYear = true) => {
  const options = {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }

  if (showYear) {
    options.year = 'numeric'
  }

  return new Intl.DateTimeFormat(locale, options).format(new Date(dateTime))
}

export const isCrossDay = (start, end = new Date()) => {
  const startDate = new Date(start)

  end.setHours(0, 0, 0, 0)

  return startDate < end
}

export const minutesToHHmm = (minutes) => {
  if (minutes == null) return ''
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`
}

export const getPropertyName = (obj, expression) => {
  var res = {}
  Object.keys(obj).forEach(key => {
    res[key] = key
  })
  return expression(res)
}

export const stringToHsl = (str) => {
  if (!str) return [0, 0, 0]

  // FNV-1a hash
  let hash = 2166136261
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i)
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24)
  }

  // Rendi il risultato unsigned
  hash >>>= 0

  // Scompone l'hash in bit separati per ogni componente
  const hue = (hash & 0xFF) + ((hash >> 8) & 0xFF) % 360 // 0–359
  const saturation = 60 + ((hash >> 16) & 0x1F) // 60–91%
  const lightness = 55 + ((hash >> 21) & 0x1F) // 55–76%

  return [hue % 360, saturation, lightness]
}

export const stringToColor = (str) => {
  const stringToHslValue = stringToHsl(str)
  return `hsl(${stringToHslValue[0]}, ${stringToHslValue[1]}%, ${stringToHslValue[2]}%)`
}

export const fixedDecimal = (value, decimals = 2) => {
  const factor = 10 ** decimals
  return Math.round((value + Number.EPSILON) * factor) / factor
}

export const getInitials = (name) => {
  if (!name) { return '' }
  return name.split(' ').map(n => n[0]).join('').toUpperCase()
}

export const updateDocumentTitle = (titleKey) => {
  const registryStore = useRegistryStore()
  const portalStore = usePortalStore()
  const { t } = i18n.global

  if (!titleKey) {
    document.title = t('common.application')
    return
  }

  i18n.global.locale.value = portalStore.selectedLanguage
  let pageTitle = t(titleKey)
  if (pageTitle === titleKey) {
    pageTitle = t('common.application')
  }
  const companyName = registryStore.companyData?.name || ''

  if (companyName) {
    document.title = `${pageTitle} - ${companyName}`
  } else {
    document.title = pageTitle
  }
}

export const isNullOrUndefined = (value) => {
  return value === null || value === undefined
}

export const getDirtyFields = (current, initial) => {
  return Object.keys(current).reduce((acc, key) => {
    if (JSON.stringify(current[key]) !== JSON.stringify(initial[key])) {
      acc[key] = current[key]
    }
    return acc
  }, {})
}

export const formatBytes = (bytes) => {
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${bytes} B`
}
